import { Clock, CheckCircle, Package, Edit2, FileText, AlertTriangle, ChevronDown, ChevronUp, Phone, Mail, Plus, Loader2, Trash2, Undo2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { JobProfitBadge } from '../../../components/ui/JobProfitBadge';
import { useOrgSettings } from '../../../hooks/useOrgSettings';
import ReturnReasonModal from './ReturnReasonModal';
import { ConfirmModal } from '../../../components/ui/ConfirmModal';
import { formatJobNumber } from '../../../lib/docNumbers';
import { VoidReissueModal } from './VoidReissueModal';
import toast from 'react-hot-toast';
import { canOpenJobInvoice, invoiceReadiness, jobWorkspaceActions } from './jobWorkflow';
import { PartTypeahead } from '../../../components/ui/PartTypeahead';
import { useVertical } from '../../../context/VerticalContext';
import { useRole } from '../../../context/RoleContext';
import { AdditionalWorkPanel } from './AdditionalWork/AdditionalWorkPanel';
import { extraWorkCardLabel } from '../../../lib/extraWork';
export interface JobItem {
    id: string;
    type: 'part' | 'labour';
    description: string;
    quantity: number;
    price: number;
    costPrice?: number;
    /** Set when the line is extra work the customer accepted: read-only. */
    approvedExtraNo?: number | null;
}

export interface Job {
    id: string;
    job_number?: number;
    invoice_number?: string;
    customer_name: string;
    customer_phone?: string;
    customer_email?: string;
    vehicle_vrm: string;
    vehicle_desc: string;
    vehicle_year?: number;
    vehicle_mileage?: number;
    vehicle_engine?: string;
    vehicle_fuel_type?: string;
    status: 'pending' | 'in_progress' | 'waiting_parts' | 'ready';
    items: JobItem[];
    total: number;
    created_at: string;
    job_description?: string;
    estimated_completion?: string | null;
    status_note?: string | null;
    invoice_id?: string;
    invoice_status?: string;
    locked?: boolean;
    is_void?: boolean;
    has_walkaround?: boolean;
}

interface JobCardProps {
    job: Job;
    viewMode: 'kanban' | 'list';
    isHighlighted?: boolean;
    onAddPart: (jobId: string, jobCreatedAt: string) => void;
    onAddLabour: (jobId: string, jobCreatedAt: string) => void;
    onComplete: (jobId: string, jobCreatedAt: string) => void;
    onGenerateInvoice?: (jobId: string, invoiceId?: string) => void;
    onWalkaround?: (jobId: string) => void;
    onDelete?: (jobId: string, createdAt: string) => void;
    onRefresh?: () => void;
    defaultExpanded?: boolean;
}

const JobItemRow = ({ item, onUpdate, onDelete, onReturn, defaultPartsMarkup, locked = false }: {
    item: JobItem;
    onUpdate: (id: string, updates: Partial<JobItem>) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
    onReturn?: (item: JobItem) => void;
    defaultPartsMarkup: number;
    locked?: boolean;
}) => {
    // Local state for immediate input feedback
    const [editData, setEditData] = useState({
        description: item.description,
        quantity: item.quantity,
        price: item.price,
        costPrice: item.costPrice || 0
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);

    // Sync from props if external update happens
    useEffect(() => {
        setEditData({
            description: item.description,
            quantity: item.quantity,
            price: item.price,
            costPrice: item.costPrice || 0
        });
    }, [item.description, item.quantity, item.price, item.costPrice]);

    const handleBlur = () => {
        // Only save if changed
        if (
            editData.description !== item.description ||
            editData.quantity !== item.quantity ||
            editData.price !== item.price ||
            editData.costPrice !== (item.costPrice || 0)
        ) {
            onUpdate(item.id, editData);
        }
    };

    const handleCostBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const newCost = parseFloat(e.target.value) || 0;
        const newPrice = Number((newCost * (1 + defaultPartsMarkup / 100)).toFixed(2));

        setEditData(prev => ({ ...prev, costPrice: newCost, price: newPrice }));
        onUpdate(item.id, { ...editData, costPrice: newCost, price: newPrice });
    };

    // Reverse of handleCostBlur: if a user types a sale price on a part WITHOUT
    // having entered a cost, default the cost so COGS posts correctly when the
    // invoice is paid. Without this, taxable profit silently over-reports.
    // Falls back to zero-margin (cost = price) when markup is 0 — always safe.
    const handlePriceBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const newPrice = parseFloat(e.target.value) || 0;
        const hasNoCost = (editData.costPrice || 0) === 0;

        if (item.type === 'part' && newPrice > 0 && hasNoCost) {
            const impliedCost = defaultPartsMarkup > 0
                ? Number((newPrice / (1 + defaultPartsMarkup / 100)).toFixed(2))
                : newPrice;

            setEditData(prev => ({ ...prev, price: newPrice, costPrice: impliedCost }));
            onUpdate(item.id, { ...editData, price: newPrice, costPrice: impliedCost });
            return;
        }

        handleBlur();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            (e.target as HTMLElement).blur();
        }
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-tint/5 rounded-lg p-2 hover:bg-tint/10 transition-colors"
            onClick={(e) => e.stopPropagation()}
        >
            {/* Type + Description + Qty + Price + Total.
                The 12-column grid applies from sm: UP ONLY. At 320px a
                col-span-1 track is ~25px, which is what made these fields
                unusable and squashed the numbers into each other. Below sm the
                row stacks with real labels. */}
            <div className="sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center min-w-0">
                {/* Type Badge */}
                <span className={`sm:col-span-1 inline-block mb-2 sm:mb-0 text-fd-3xs font-bold uppercase ${item.type === 'part' ? 'text-info' : 'text-gold'
                    }`}>
                    {item.type}
                    {item.approvedExtraNo ? <span className="ml-2 sm:ml-0 sm:block text-success normal-case">{extraWorkCardLabel(item.approvedExtraNo)}</span> : null}
                </span>

                {/* The description was a bare text box holding the word "Part",
                    which every user deleted before typing the real name. It is
                    now the same typeahead the quote and diagnostic screens use:
                    still a plain text box you can type anything into, that
                    offers this workshop's own parts (with their prices) and a
                    shared dictionary while you type. Empty is empty — the
                    placeholder says what to do instead of pretending to be a
                    value. */}
                {locked ? (
                    <input
                        type="text"
                        /* A locked row is a record, not a field: show the type
                           rather than an empty box if it was never described. */
                        value={editData.description || (item.type === 'labour' ? 'Labour' : 'Part')}
                        readOnly
                        className="w-full sm:col-span-4 bg-canvas sm:bg-transparent border border-tint/10 sm:border-none text-fg text-sm outline-none rounded-lg sm:rounded px-2 sm:px-1 py-2 sm:py-0 min-h-[44px] sm:min-h-0 mb-2 sm:mb-0 opacity-70"
                    />
                ) : (
                    <PartTypeahead
                        value={editData.description}
                        onChange={v => setEditData(prev => ({ ...prev, description: v }))}
                        onBlur={handleBlur}
                        onPick={part => {
                            // Saved straight away rather than on the next blur:
                            // picking a suggestion moves focus off the field, so
                            // there may not be another blur to save on.
                            const next = {
                                ...editData,
                                description: part.name,
                                // Never overwrite a figure already typed for this job.
                                ...(part.retailPrice != null && !editData.price
                                    ? { price: Number(part.retailPrice) } : {}),
                                ...(part.costPrice != null && !editData.costPrice
                                    ? { costPrice: Number(part.costPrice) } : {}),
                            };
                            setEditData(next);
                            onUpdate(item.id, next);
                        }}
                        itemType={item.type}
                        placeholder={item.type === 'part' ? 'Start typing a part…' : 'What was done…'}
                        className="w-full sm:col-span-4 mb-2 sm:mb-0"
                    />
                )}

                {/* Numbers get their own 3-up row on a phone. */}
                <div className="grid grid-cols-3 gap-2 sm:contents">

                {/* Cost Input (Parts only) */}
                {item.type === 'part' ? (
                    <label className="flex flex-col gap-1 sm:contents">
                    <span className="sm:hidden text-fd-3xs uppercase tracking-wider text-fg-4 font-display">Cost</span>
                    <input
                        type="number"
                        value={editData.costPrice || ''}
                        onChange={e => setEditData({ ...editData, costPrice: parseFloat(e.target.value) || 0 })}
                        onBlur={handleCostBlur}
                        onKeyDown={handleKeyDown}
                        step="0.01"
                        min="0"
                        readOnly={locked}
                        className="w-full sm:col-span-2 bg-canvas sm:bg-transparent border border-tint/10 rounded-lg sm:rounded text-fg text-sm sm:text-right outline-none focus:ring-1 focus:ring-accent py-2 sm:py-1 px-2 min-h-[44px] sm:min-h-0 read-only:opacity-70"
                        title={`Auto-adds ${defaultPartsMarkup}% markup`}
                    />
                    </label>
                ) : (
                    <div className="hidden sm:block sm:col-span-2" />
                )}

                {/* Quantity Input */}
                <label className="flex flex-col gap-1 sm:contents">
                <span className="sm:hidden text-fd-3xs uppercase tracking-wider text-fg-4 font-display">Qty</span>
                <input
                    type="number"
                    value={editData.quantity}
                    onChange={e => setEditData({ ...editData, quantity: parseFloat(e.target.value) || 0 })}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    step="0.5"
                    min="0"
                    readOnly={locked}
                    className="w-full sm:col-span-1 bg-canvas sm:bg-transparent border border-tint/10 rounded-lg sm:rounded text-fg text-sm text-center outline-none focus:ring-1 focus:ring-accent py-2 sm:py-1 min-h-[44px] sm:min-h-0 read-only:opacity-70"
                />
                </label>

                {/* Price Input */}
                <label className="flex flex-col gap-1 sm:contents">
                <span className="sm:hidden text-fd-3xs uppercase tracking-wider text-fg-4 font-display">Sell</span>
                <input
                    type="number"
                    value={editData.price || ''}
                    onChange={e => setEditData({ ...editData, price: parseFloat(e.target.value) || 0 })}
                    onBlur={handlePriceBlur}
                    onKeyDown={handleKeyDown}
                    step="0.01"
                    min="0"
                    readOnly={locked}
                    className="w-full sm:col-span-2 bg-canvas sm:bg-transparent border border-tint/10 rounded-lg sm:rounded text-fg text-sm sm:text-right outline-none focus:ring-1 focus:ring-accent py-2 sm:py-1 px-2 min-h-[44px] sm:min-h-0 read-only:opacity-70"
                    title={item.type === 'part' && (editData.costPrice || 0) === 0
                        ? `Tip: enter cost first; or this will default cost from ${defaultPartsMarkup}% markup`
                        : undefined}
                />
                </label>
                </div>

                {/* Total Display */}
                <span className="sm:col-span-2 flex sm:block justify-between items-center mt-3 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-tint/5 text-sm font-mono font-bold sm:font-normal text-fg sm:text-fg-2 sm:text-right tabular-nums">
                    <span className="sm:hidden text-fd-3xs uppercase tracking-wider text-fg-4 font-display font-normal">Total</span>
                    £{(editData.quantity * editData.price).toFixed(2)}
                </span>
            </div>

            {/* Bottom row: Labeled action buttons (hidden once the job is locked) */}
            <div className={`flex justify-end gap-2 mt-1.5 pt-1.5 border-t border-tint/5 ${locked ? 'hidden' : ''}`}>
                {/* Return button — only for parts */}
                {item.type === 'part' && onReturn && (
                    <button
                        onClick={() => onReturn(item)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gold-fill/10 text-gold hover:bg-gold-fill/20 text-fd-sm-tight font-bold transition-colors"
                    >
                        <Undo2 className="w-4 h-4 sm:w-3 sm:h-3" />
                        RETURN
                    </button>
                )}
                <button
                    onClick={() => setItemToDelete(item.id)}
                    disabled={isDeleting}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-danger/10 text-danger hover:bg-danger/20 text-fd-sm-tight font-bold transition-colors disabled:opacity-50"
                >
                    {isDeleting ? <Loader2 className="w-4 h-4 sm:w-3 sm:h-3 animate-spin" /> : <Trash2 className="w-4 h-4 sm:w-3 sm:h-3" />}
                    DELETE
                </button>
            </div>

            <ConfirmModal
                isOpen={!!itemToDelete}
                onClose={() => setItemToDelete(null)}
                // We use async/await trick or just sync execute depending on onDelete signature.
                // Since this uses an async onDelete we wait or just fire.
                onConfirm={() => {
                    if (itemToDelete) {
                        setIsDeleting(true);
                        onDelete(itemToDelete);
                        setItemToDelete(null);
                    }
                }}
                title="Delete Item"
                message="Are you sure you want to delete this item? This action cannot be undone."
                confirmText="Delete"
                isDangerous={true}
            />
        </motion.div>
    );
};

export default function JobCard({ job, viewMode, isHighlighted, onAddPart, onAddLabour, onComplete: _onComplete, onGenerateInvoice, onWalkaround, onDelete, onRefresh, defaultExpanded = false }: JobCardProps) {
    const isList = viewMode === 'list';
    const { settings: orgSettings } = useOrgSettings();
    const { activeOrgId } = useVertical();
    const { isOwner, isMechanic } = useRole();
    const [financials, setFinancials] = useState<{
        gross_profit: number;
        margin_percent: number;
        subtotal_net: number;
        vat_applicable: boolean;
        vat_rate: number;
        vat_amount: number;
        grand_total: number;
    } | null>(null);
    const [loadingFin, setLoadingFin] = useState(false);
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    // Lines "+ Part" saved but nobody filled in. The invoice generator refuses
    // them; this says so on the card, before the button is pressed.
    const unfinishedLines = new Set(invoiceReadiness(job.items).blocking.map((b) => b.line)).size;
    const workspaceActions = jobWorkspaceActions(job, {
        onAddPart,
        onAddLabour,
        onGenerateInvoice,
        onWalkaround,
    });
    const [showVoidModal, setShowVoidModal] = useState(false);
    const [returnModalItem, setReturnModalItem] = useState<JobItem | null>(null);

    // Job value + VAT are decided by the backend (fn_get_job_financials), which
    // applies the single HMRC EDR rule against the job's calendar date. The
    // frontend does no VAT logic and never recomputes it from current settings.
    const subtotal = financials?.subtotal_net ?? 0;
    const isVatApplicable = financials?.vat_applicable ?? false;
    const vatAmount = financials?.vat_amount ?? 0;
    const grandTotal = financials?.grand_total ?? 0;

    // Description Editing State
    const [description, setDescription] = useState(job.job_description || '');
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [savingDescription, setSavingDescription] = useState(false);

    useEffect(() => {
        setDescription(job.job_description || '');
    }, [job.job_description]);

    // Hold-up note: why this job is taking longer than planned, plus a revised
    // date. Separate from the description because the description is what the
    // work IS, and this is what's gone wrong with it.
    const [statusNote, setStatusNote] = useState(job.status_note || '');
    const [dueDate, setDueDate] = useState(
        job.estimated_completion ? job.estimated_completion.slice(0, 10) : ''
    );
    const [savingNote, setSavingNote] = useState(false);

    useEffect(() => {
        setStatusNote(job.status_note || '');
        setDueDate(job.estimated_completion ? job.estimated_completion.slice(0, 10) : '');
    }, [job.status_note, job.estimated_completion]);

    const handleSaveNote = async () => {
        setSavingNote(true);
        try {
            const { error } = await supabase
                .from('jobs')
                .update({
                    status_note: statusNote.trim() || null,
                    estimated_completion: dueDate ? new Date(dueDate).toISOString() : null,
                })
                .eq('id', job.id)
                .eq('created_at', job.created_at);
            if (error) throw error;
            onRefresh?.();
        } catch {
            toast.error('Failed to save the hold-up note');
        } finally {
            setSavingNote(false);
        }
    };

    const handleSaveDescription = async () => {
        setSavingDescription(true);
        try {
            const { error } = await supabase
                .from('jobs')
                .update({ job_description: description })
                .eq('id', job.id)
                .eq('created_at', job.created_at); // Composite Key for Partition Pruning

            if (error) throw error;
            setIsEditingDescription(false);
        } catch {
            toast.error('Failed to save description');
        } finally {
            setSavingDescription(false);
        }
    };

    useEffect(() => {
        const fetchFin = async () => {
            setLoadingFin(true);
            const { data, error } = await supabase.rpc('fn_get_job_financials', { p_job_id: job.id });
            if (!error && data) {
                setFinancials(data as any);
            }
            setLoadingFin(false);
        };
        fetchFin();
    }, [job.id, job.items]);

    const statusColors = {
        pending: 'bg-raised text-fg-3 border-line',
        // Copper: work happening now (fd-live adds the breathing ember).
        in_progress: 'fd-live',
        waiting_parts: 'bg-warning/10 text-warning border-warning/20',
        ready: 'bg-success/10 text-success border-success/20',
    };

    const handleCardClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button')) return;
        setIsExpanded(!isExpanded);
    };

    return (
        <motion.div
            id={`job-card-${job.id}`}
            layout
            className={`
                group rounded-2xl transition-all cursor-pointer overflow-hidden
                ${isHighlighted
                    ? 'bg-raised border border-gold shadow-[0_0_0_1px_var(--fd-gold-line),var(--fd-shadow-2)]'
                    : 'bg-card border border-line shadow-soft hover:border-gold/40 hover:bg-raised'
                }
            `}
            onClick={handleCardClick}
        >
            {/* Main Row - Clean Overview */}
            <div className={`p-5 flex flex-col lg:flex-row lg:items-center gap-5`}>

                {/* Left: Vehicle & Customer Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-2">
                        {job.job_number != null && (
                            <span className="inline-flex px-2 py-0.5 rounded text-fd-sm-tight font-mono font-bold tracking-wider bg-card text-fg-3 border border-line">
                                {formatJobNumber(job.job_number)}
                            </span>
                        )}
                        <span
                            className="inline-flex px-2 py-0.5 rounded text-xs font-mono font-bold tracking-wider"
                            style={{ backgroundColor: 'var(--fd-plate)', color: 'var(--fd-plate-ink)' }}
                        >
                            {job.vehicle_vrm}
                        </span>
                        <span className={`px-2 py-0.5 rounded-md text-fd-3xs font-bold uppercase tracking-wider border ${statusColors[job.status]}`}>
                            {job.status.replace('_', ' ')}
                        </span>
                        {job.is_void ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-fd-3xs font-bold uppercase tracking-wider bg-danger/10 text-danger border border-danger/25" title="This job was voided and reissued.">
                                Void
                            </span>
                        ) : job.locked && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-fd-3xs font-bold uppercase tracking-wider bg-success/10 text-success border border-success/25" title="Invoice paid — this job is locked. Void & reissue to make changes.">
                                <CheckCircle className="w-3 h-3" /> Paid · Locked
                            </span>
                        )}
                        {job.invoice_number && (
                            <span className="text-fd-3xs font-mono font-semibold text-fg-4">
                                {job.invoice_number}
                            </span>
                        )}
                        <span className="text-fd-3xs font-medium text-fg-4 ml-auto lg:ml-0">
                            {new Date(job.created_at).toLocaleDateString('en-GB')}
                        </span>
                    </div>
                    <h3 className="text-fg text-base font-bold truncate">{job.customer_name}</h3>
                    <p className="text-fg-3 text-xs truncate">{job.vehicle_desc}</p>
                </div>

                {/* Center: Job Value */}
                <div className="flex items-center justify-between lg:justify-center gap-6 py-4 lg:py-0 border-y border-line lg:border-y-0 lg:border-x lg:px-8">
                    <div className="flex flex-col">
                        <span className="text-fd-3xs font-display font-semibold text-fg-4 uppercase tracking-wider mb-1">
                            Job Value {loadingFin ? '' : isVatApplicable ? '(Inc. VAT)' : ''}
                        </span>
                        {loadingFin ? (
                            <div className="h-7 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-gold" /></div>
                        ) : (
                            <span className="text-xl font-mono font-bold text-gold">
                                £{grandTotal.toFixed(2)}
                            </span>
                        )}
                    </div>
                    {!isList && (
                        <JobProfitBadge
                            grossProfit={financials?.gross_profit || 0}
                            marginPercent={financials?.margin_percent || 0}
                            loading={loadingFin}
                        />
                    )}
                </div>

                {/* Right: Actions */}
                <div className="flex flex-wrap items-center justify-end gap-2 lg:gap-3">
                    {isList && (
                        <div className="hidden sm:block mr-2">
                            <JobProfitBadge
                                grossProfit={financials?.gross_profit || 0}
                                marginPercent={financials?.margin_percent || 0}
                                loading={loadingFin}
                            />
                        </div>
                    )}

                    <div className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                        {onDelete && !job.locked && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(job.id, job.created_at); }}
                                className="flex-1 sm:flex-none flex items-center justify-center gap-2 h-11 w-full sm:w-11 rounded-lg text-fg-3 hover:text-danger hover:bg-danger/10 transition-colors z-10"
                                title="Delete Job"
                            >
                                <Trash2 className="w-4 h-4" />
                                <span className="text-sm font-semibold inline sm:hidden">Delete</span>
                            </button>
                        )}
                        <button
                            onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 h-11 w-full sm:w-11 rounded-lg bg-card hover:bg-raised border border-line text-fg-3 hover:text-fg transition-colors z-10"
                            title={isExpanded ? 'Collapse Job' : 'Expand Job'}
                        >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            <span className="text-sm font-semibold inline sm:hidden">{isExpanded ? 'Collapse' : 'Expand'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Expanded Details Section */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-tint/10 bg-tint/[0.02] overflow-hidden"
                    >
                        <div className="p-4 space-y-4">
                            {job.is_void && (
                                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-danger/5 border border-danger/20 text-danger/90 text-xs">
                                    <AlertTriangle className="w-4 h-4 shrink-0" />
                                    <span>
                                        This job was <strong>voided</strong> and reissued as an editable copy. It's kept
                                        read-only for the audit trail; its income was reversed in the ledger.
                                    </span>
                                </div>
                            )}
                            {job.locked && !job.is_void && (
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-3 py-2.5 rounded-lg bg-success/5 border border-success/20 text-success/90 text-xs">
                                    <CheckCircle className="w-4 h-4 shrink-0" />
                                    <span className="flex-1">
                                        Invoice {job.invoice_number || ''} is paid, so this job is locked. To change
                                        anything, use <strong>Void &amp; Reissue</strong> — it cancels this job and
                                        creates an editable copy, keeping the audit trail intact.
                                    </span>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setShowVoidModal(true); }}
                                        className="shrink-0 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-gold-fill/15 text-gold hover:bg-gold-fill/25 border border-gold/30 font-bold text-fd-sm-tight transition-colors z-10"
                                    >
                                        <Undo2 className="w-3.5 h-3.5" />
                                        VOID &amp; REISSUE
                                    </button>
                                </div>
                            )}
                            {/* Action Buttons - Add Parts/Labour/Walkaround */}
                            <div className={`flex flex-col sm:flex-row flex-wrap gap-2 ${job.locked ? 'hidden' : ''}`}>
                                <button
                                    onClick={(e) => { e.stopPropagation(); workspaceActions.addPart(); }}
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 sm:px-3 sm:py-2 rounded-lg bg-info/10 text-info hover:bg-info/20 font-bold text-xs transition-colors z-10"
                                >
                                    <Plus className="w-5 h-5 sm:w-3 sm:h-3" />
                                    <Package className="w-6 h-6 sm:w-4 sm:h-4" />
                                    <span>ADD PART</span>
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); workspaceActions.addLabour(); }}
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 sm:px-3 sm:py-2 rounded-lg bg-gold-fill/10 text-gold hover:bg-gold-fill/20 font-bold text-xs transition-colors z-10"
                                >
                                    <Plus className="w-5 h-5 sm:w-3 sm:h-3" />
                                    <Clock className="w-6 h-6 sm:w-4 sm:h-4" />
                                    <span>ADD LABOUR</span>
                                </button>
                                {onWalkaround && (job.status === 'pending' || job.status === 'in_progress') && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); workspaceActions.walkaround(); }}
                                        className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 sm:px-3 sm:py-2 rounded-lg font-bold text-xs transition-colors z-10 relative ${job.has_walkaround
                                                ? 'bg-success/10 text-success hover:bg-success/20'
                                                : 'bg-warning/10 text-warning hover:bg-warning/20'
                                            }`}
                                    >
                                        <AlertTriangle className="w-6 h-6 sm:w-4 sm:h-4" />
                                        <span>WALKAROUND</span>
                                        {job.has_walkaround && (
                                            <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-fill opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-3 w-3 bg-success-fill"></span>
                                            </span>
                                        )}
                                    </button>
                                )}
                            </div>

                            {/* What the customer has agreed to: the original quote plus
                                any extra work they accepted. Extra work is added here
                                and sent for approval; only accepted work joins the
                                items below (and so the invoice). */}
                            {!job.is_void && activeOrgId && (
                                <AdditionalWorkPanel
                                    jobId={job.id}
                                    orgId={activeOrgId}
                                    canPriceWork={isOwner || isMechanic}
                                    refreshKey={job.items}
                                    onChanged={onRefresh}
                                />
                            )}

                            {/* Items List - Quote Style Grid */}
                            <div className="space-y-2">
                                {/* Hidden below sm, because the rows themselves
                                    stack there and carry their own labels — a
                                    12-column header above a stacked card is just
                                    seven squashed words. */}
                                <div className="hidden sm:grid grid-cols-12 gap-2 text-fd-3xs font-bold text-fg-4 uppercase px-2">
                                    <span className="col-span-1">Type</span>
                                    <span className="col-span-4">Description</span>
                                    <span className="col-span-2 text-right">Cost £</span>
                                    <span className="col-span-1 text-center">Qty</span>
                                    <span className="col-span-2 text-right">Sell £</span>
                                    <span className="col-span-1 text-right">Total</span>
                                    <span className="col-span-1"></span>
                                </div>

                                <div className="space-y-1">
                                    {job.items.length === 0 ? (
                                        <div className="text-center py-4 text-fg-4 text-xs italic bg-tint/5 rounded-lg">
                                            No items added yet. Click "+ PART" or "+ LABOUR" above.
                                        </div>
                                    ) : (
                                        job.items.map(item => (
                                            <JobItemRow
                                                key={item.id}
                                                item={item}
                                                // Approved extra work is what the customer said yes to;
                                                // the database refuses edits, so the row is read-only.
                                                locked={job.locked || !!item.approvedExtraNo}
                                                defaultPartsMarkup={orgSettings.default_parts_markup || 0}
                                                onReturn={(item) => setReturnModalItem(item)}
                                                onUpdate={async (id, updates) => {
                                                    try {
                                                        const { error } = await supabase
                                                            .from('job_items')
                                                            .update({
                                                                // NULL, not '': the invoice snapshot
                                                                // falls back with
                                                                // COALESCE(name, description, item_type),
                                                                // and an empty string wins that COALESCE
                                                                // and prints a blank invoice line.
                                                                description: updates.description?.trim() || null,
                                                                quantity: updates.quantity,
                                                                unit_price: updates.price,
                                                                cost_price: updates.costPrice
                                                            })
                                                            .eq('id', id);

                                                        if (error) throw error;
                                                        if (onRefresh) onRefresh();
                                                    } catch (err) {
                                                        console.error('Job status update failed:', err);
                                                    }
                                                }}
                                                onDelete={async (id) => {
                                                    try {
                                                        const { error } = await supabase
                                                            .from('job_items')
                                                            .delete()
                                                            .eq('id', id);

                                                        if (error) throw error;
                                                        if (onRefresh) onRefresh();
                                                    } catch (err) {
                                                        console.error('Job status update failed:', err);
                                                    }
                                                }}
                                            />
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Job Value Footer */}
                            {job.items.length > 0 && (
                                <div className="border-t border-tint/5 pt-2 space-y-1">
                                    <div className="flex justify-between text-xs text-fg-4">
                                        <span>Subtotal</span>
                                        <span className="font-mono">£{subtotal.toFixed(2)}</span>
                                    </div>
                                    {isVatApplicable && (
                                        <div className="flex justify-between text-xs text-fg-4">
                                            <span>VAT (20%)</span>
                                            <span className="font-mono">£{vatAmount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-sm font-bold text-fg pt-1 border-t border-tint/5">
                                        <span>Total</span>
                                        <span className="font-mono text-accent">£{grandTotal.toFixed(2)}</span>
                                    </div>
                                </div>
                            )}

                            {/* Customer & Vehicle Info Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <h4 className="text-xs font-bold text-fg-4 uppercase">Customer</h4>
                                    <div className="space-y-1">
                                        <p className="text-sm text-fg font-medium">{job.customer_name}</p>
                                        {job.customer_phone && (
                                            <p className="text-xs text-fg-3 flex items-center gap-1">
                                                <Phone className="w-3 h-3" />
                                                <a href={`tel:${job.customer_phone}`} className="hover:text-fg" onClick={e => e.stopPropagation()}>
                                                    {job.customer_phone}
                                                </a>
                                            </p>
                                        )}
                                        {job.customer_email && (
                                            <p className="text-xs text-fg-3 flex items-center gap-1">
                                                <Mail className="w-3 h-3" />
                                                <a href={`mailto:${job.customer_email}`} className="hover:text-fg" onClick={e => e.stopPropagation()}>
                                                    {job.customer_email}
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <h4 className="text-xs font-bold text-fg-4 uppercase">Vehicle</h4>
                                    <div className="space-y-1">
                                        <p className="text-sm text-fg font-mono">{job.vehicle_vrm}</p>
                                        <p className="text-xs text-fg-3">{job.vehicle_desc}</p>
                                        <div className="flex gap-4 text-xs text-fg-4">
                                            {job.vehicle_year && <span>Year: {job.vehicle_year}</span>}
                                            {job.vehicle_mileage && <span>Mileage: {job.vehicle_mileage.toLocaleString()}</span>}
                                            {job.vehicle_engine && <span>Engine: {job.vehicle_engine}</span>}
                                            {job.vehicle_fuel_type && <span>Fuel: {job.vehicle_fuel_type}</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Job Description */}
                            {/* Job Description - Editable */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-bold text-fg-4 uppercase">Job Description</h4>
                                    {!isEditingDescription && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setIsEditingDescription(true); }}
                                            className="text-xs text-accent hover:text-fg flex items-center gap-1"
                                        >
                                            <Edit2 className="w-3 h-3" />
                                            Edit
                                        </button>
                                    )}
                                </div>

                                {isEditingDescription ? (
                                    <div className="space-y-2">
                                        <textarea
                                            value={description}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => setDescription(e.target.value)}
                                            className="w-full bg-scrim/20 border border-tint/10 rounded-lg p-3 text-sm text-fg focus:ring-2 focus:ring-accent outline-none resize-none"
                                            rows={3}
                                            placeholder="Enter job description..."
                                            autoFocus
                                        />
                                        <div className="flex gap-2 justify-end">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setIsEditingDescription(false); setDescription(job.job_description || ''); }}
                                                className="px-3 py-1.5 text-xs text-fg-3 hover:text-fg"
                                                disabled={savingDescription}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleSaveDescription(); }}
                                                disabled={savingDescription}
                                                className="px-3 py-1.5 text-xs fd-btn-primary rounded font-bold flex items-center gap-2"
                                            >
                                                {savingDescription && <Loader2 className="w-3 h-3 animate-spin" />}
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <p
                                        className="text-sm text-fg-2 bg-tint/5 rounded p-3 whitespace-pre-wrap cursor-pointer hover:bg-tint/10 transition-colors"
                                        onClick={(e) => { e.stopPropagation(); setIsEditingDescription(true); }}
                                    >
                                        {description || <span className="text-fg-4 italic">No description provided. Click to add one.</span>}
                                    </p>
                                )}
                            </div>

                            {/* Running late? — one sentence and a revised date.
                                Deliberately not a scheduling engine: the useful
                                unit is a note a human writes and reads. */}
                            <div className="space-y-2">
                                <h4 className="text-xs font-bold text-fg-4 uppercase">Running late?</h4>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <input
                                        type="text"
                                        value={statusNote}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => setStatusNote(e.target.value)}
                                        placeholder="e.g. waiting on rear calliper, due Thursday"
                                        className="flex-1 bg-scrim/20 border border-tint/10 rounded-lg px-3 py-2 text-sm text-fg focus:ring-2 focus:ring-accent outline-none"
                                    />
                                    <input
                                        type="date"
                                        value={dueDate}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => setDueDate(e.target.value)}
                                        aria-label="Expected completion date"
                                        className="bg-scrim/20 border border-tint/10 rounded-lg px-3 py-2 text-sm text-fg focus:ring-2 focus:ring-accent outline-none"
                                    />
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleSaveNote(); }}
                                        disabled={savingNote}
                                        className="px-4 py-2 text-xs bg-tint/10 hover:bg-tint/20 text-fg rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                                    >
                                        {savingNote && <Loader2 className="w-3 h-3 animate-spin" />}
                                        Save
                                    </button>
                                </div>
                                <p className="text-fd-sm-tight text-fg-4">
                                    Shows on your dashboard job list, so a job that slips from hours to days
                                    still reads correctly at a glance.
                                </p>
                            </div>

                            {/* Invoicing is the completion step, so its primary action
                                belongs after the work, line items, totals and notes. */}
                            {onGenerateInvoice && canOpenJobInvoice(job) && (
                                <div className="pt-4 border-t border-tint/10">
                                    {!job.invoice_id && unfinishedLines > 0 && (
                                        <p className="mb-3 flex items-start gap-2 text-sm text-warning" role="status">
                                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                                            <span>
                                                {unfinishedLines === 1 ? '1 line was added but not filled in' : `${unfinishedLines} lines were added but not filled in`}.
                                                Finish {unfinishedLines === 1 ? 'it' : 'them'} before invoicing.
                                            </span>
                                        </p>
                                    )}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); workspaceActions.invoice(); }}
                                        // Money in is the job's last step, whether the invoice
                                        // is still to generate or already exists: brass, the money
                                        // metal, the same as Invoice → on the dashboard.
                                        className="fd-btn fd-btn-brass fd-btn-lg w-full"
                                    >
                                        <FileText className="w-4 h-4" />
                                        <span>{job.invoice_id ? 'Open Invoice' : 'Generate Invoice'}</span>
                                    </button>
                                </div>
                            )}

                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Return Reason Modal */}
            <ReturnReasonModal
                isOpen={!!returnModalItem}
                onClose={() => setReturnModalItem(null)}
                itemId={returnModalItem?.id || ''}
                itemDescription={returnModalItem?.description || (returnModalItem?.type === 'labour' ? 'Labour' : 'Part')}
                itemPrice={returnModalItem?.price || 0}
                itemQuantity={returnModalItem?.quantity || 0}
                jobId={job.id}
                jobCreatedAt={job.created_at}
                vehicleVrm={job.vehicle_vrm}
                onSuccess={() => {
                    setReturnModalItem(null);
                    if (onRefresh) onRefresh();
                }}
            />

            {showVoidModal && (
                <VoidReissueModal
                    jobId={job.id}
                    jobLabel={formatJobNumber(job.job_number)}
                    onClose={() => setShowVoidModal(false)}
                    onVoided={() => {
                        setShowVoidModal(false);
                        if (onRefresh) onRefresh();
                    }}
                />
            )}
        </motion.div>
    );
}
